/**
 * captureFormMemory — persiste o estado do formulário de captura, por módulo (porta do
 * `sync.search[module]` do legado, gravado via `saveInput` a cada mudança).
 *
 * Grava em DOIS lugares, com papéis distintos:
 *
 *  1. **localStorage** — reabrir o modal traz o formulário como o usuário deixou, mesmo sem
 *     confirmar a busca e mesmo após fechar o navegador. É o dono do formato rico (ComboOption).
 *
 *  2. **chrome.storage.sync (`search[module]`)** — a EXTENSÃO lê daqui, e não é cosmético:
 *     - `search.phones.area_code` filtra os telefones capturados pelo DDD escolhido
 *       [content.js:3100 e 3384];
 *     - `search.phones.country` define o país usado no parse dos números [content.js:3209];
 *     - `search.phones.country` / `.area_code` viajam no payload enviado ao backend
 *       [content.js:2939-2952];
 *     - `search.<module>.keywords` aparece na celebração de fim de captura [content.js:3631].
 *     Sem essa chave, os telefones não são filtrados por DDD e o parse pode usar o país errado.
 *
 * O formato do espelho segue o legado, porque é o que a extensão sabe ler: chaves em
 * snake_case (`area_code`) e valores achatados em string/array — não o ComboOption.
 */
import type { ListModule } from '../config/constants';
import type { ComboOption } from '../components/capture/ComboInput';
import { setSync, getSync } from './extensionBridge';

/** Campos memorizados (união de todos os módulos; cada um usa os seus). */
export interface CaptureFormMemory {
  /** Texto cru do campo de palavras-chave (o split por `|` acontece no submit). */
  keywords?: string;
  sites?: string[];
  domains?: string[];
  engine?: string;
  country?: ComboOption | null;
  areaCode?: ComboOption | null;
  ignore?: boolean;
  /** places: tags `geo|nome`. */
  places?: string[];
  /** places: raio em km. */
  distance?: number;
}

const KEY_PREFIX = 'capture_form_';

function storageKey(module: ListModule): string {
  return KEY_PREFIX + module;
}

/** Lê o estado salvo de um módulo (objeto vazio se nada gravado / storage indisponível). */
export function loadCaptureForm(module: ListModule): CaptureFormMemory {
  try {
    const raw = localStorage.getItem(storageKey(module));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as CaptureFormMemory) : {};
  } catch {
    return {};
  }
}

/**
 * Achata a memória para o formato que a EXTENSÃO lê em `sync.search[module]`.
 *
 * Nomes e formatos vêm do legado, não do app2:
 *  - `area_code` (snake_case) e não `areaCode`;
 *  - `country` é o ISO em minúsculas — a extensão faz `.toUpperCase()` [content.js:3210];
 *  - `countryName` é o rótulo, usado pelo popup [popup.js:1924];
 *  - `keywords` é o texto cru, como o usuário digitou.
 *
 * Só entram chaves com valor: o legado guardava cada campo atrás de um `if (value)`, e
 * gravar `undefined` apagaria o que a extensão já tinha.
 */
/** Países em que a extensão filtra os números pelo código de área corretamente. */
const EXTENSION_AREA_FILTER = new Set(['br', 'us', 'ca']);

function toSyncSearch(memory: CaptureFormMemory): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  if (memory.keywords) out['keywords'] = memory.keywords;
  if (memory.sites && memory.sites.length > 0) out['sites'] = memory.sites;
  if (memory.domains && memory.domains.length > 0) out['domains'] = memory.domains;
  if (memory.engine) out['engine'] = memory.engine;
  if (memory.places && memory.places.length > 0) out['places'] = memory.places;
  if (memory.distance != null) out['distance'] = memory.distance;
  if (memory.ignore != null) out['ignore'] = memory.ignore;

  if (memory.country) {
    out['country'] = memory.country.value;
    out['countryName'] = memory.country.text;
  }
  // A extensão corta no primeiro "-" (`"48 - Florianópolis/SC"` → `"48"`), então tanto o
  // rótulo quanto o valor puro funcionam. Guardamos o rótulo, que é o que o popup exibe.
  if (memory.areaCode) out['area_code'] = memory.areaCode.text;
  // "Capturar qualquer número": a extensão não lê `ignore` e descarta todo número de outro DDD
  // sempre que há `area_code` [content.js:3359, 3498]. Ligado, o filtro precisa ir vazio — e
  // esta é a exceção à regra acima: o vazio tem de sobrescrever o DDD já gravado. As pesquisas
  // continuam com o DDD (o schedule vem do backend com o `area_code` do POST). O mesmo vale
  // para "Todos os DDDs" (null): sem o vazio, a extensão seguiria filtrando pelo DDD anterior.
  if (memory.ignore || memory.areaCode === null) out['area_code'] = '';
  // Fora de Brasil, EUA e Canadá a extensão não sabe medir o código de área do número (pega 4
  // dígitos [content.js:3507]) e descartaria quase todo lead. Lá o código só orienta as
  // pesquisas; o filtro vai vazio até a extensão aprender os outros países.
  const iso = memory.country?.value ?? '';
  if (memory.areaCode && iso && !EXTENSION_AREA_FILTER.has(iso)) {
    out['area_code'] = '';
  }

  return out;
}

/**
 * Espera antes de espelhar no sync.
 *
 * O legado gravava no `blur` do campo [application.js:9843]; aqui o `saveCaptureForm` roda a
 * cada mudança de estado, ou seja, a cada TECLA digitada. `chrome.storage.sync` tem cota
 * (120 escritas/min) — digitar uma palavra-chave estouraria o limite e o Chrome passaria a
 * recusar as gravações. O atraso agrupa a rajada; o localStorage segue imediato.
 */
const SYNC_MIRROR_DELAY_MS = 600;

let mirrorTimer: ReturnType<typeof setTimeout> | undefined;
/** Último estado por módulo, para o disparo atrasado enviar o valor mais recente. */
const pendingMirror = new Map<ListModule, CaptureFormMemory>();

/**
 * Espelha o módulo em `sync.search[module]`, preservando os demais módulos.
 *
 * Precisa LER o sync antes de escrever: `setSync` substitui a chave `search` inteira, então
 * gravar só o módulo atual apagaria a memória dos outros — e a extensão perderia, por exemplo,
 * o DDD de phones ao mexer no formulário de emails.
 */
async function flushMirror(): Promise<void> {
  const batch = new Map(pendingMirror);
  pendingMirror.clear();
  if (batch.size === 0) return;

  try {
    const sync = await getSync<Record<string, unknown>>();
    const search = { ...((sync?.['search'] ?? {}) as Record<string, unknown>) };
    for (const [module, memory] of batch) {
      const previous = (search[module] ?? {}) as Record<string, unknown>;
      search[module] = { ...previous, ...toSyncSearch(memory) };
    }
    setSync({ search });
  } catch {
    // Sem extensão (ou bridge sem resposta): o localStorage já guardou o formulário.
  }
}

function mirrorToSync(module: ListModule, memory: CaptureFormMemory): void {
  pendingMirror.set(module, memory);
  clearTimeout(mirrorTimer);
  mirrorTimer = setTimeout(() => void flushMirror(), SYNC_MIRROR_DELAY_MS);
}

/** Força o espelhamento pendente agora — usado antes de iniciar a captura. */
export async function flushCaptureFormToSync(): Promise<void> {
  clearTimeout(mirrorTimer);
  await flushMirror();
}

/** Mescla `patch` no estado salvo do módulo (grava a cada mudança de campo). */
export function saveCaptureForm(module: ListModule, patch: Partial<CaptureFormMemory>): void {
  const current = loadCaptureForm(module);
  const merged = { ...current, ...patch };
  try {
    localStorage.setItem(storageKey(module), JSON.stringify(merged));
  } catch {
    // localStorage indisponível — só não persiste localmente; o espelho no sync ainda vale.
  }
  mirrorToSync(module, merged);
}
